import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  listPendingRequests,
  approveRequest,
  denyRequest,
  type AccessRequest,
} from '../api/requests';
import { DataTable, type Column } from './common/DataTable';
import { StatusBadge } from './common/StatusBadge';
import { ConfirmDialog } from './common/ConfirmDialog';
import { Modal } from './common/Modal';

export function RequestsView() {
  const { getJwtToken } = useAuth();

  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Approve confirmation
  const [approveTarget, setApproveTarget] = useState<AccessRequest | null>(null);

  // Deny modal
  const [denyTarget, setDenyTarget] = useState<AccessRequest | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }
      const data = await listPendingRequests(token);
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async () => {
    if (!approveTarget) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await approveRequest(approveTarget.email, token);
      setSuccess(`Approved access for ${approveTarget.email}`);
      setApproveTarget(null);
      fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
      setApproveTarget(null);
    }
  };

  const handleDeny = async () => {
    if (!denyTarget) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await denyRequest(denyTarget.email, token, denyReason || undefined);
      setSuccess(`Denied access for ${denyTarget.email}`);
      setDenyTarget(null);
      setDenyReason('');
      fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny request');
      setDenyTarget(null);
      setDenyReason('');
    }
  };

  const columns: Column<AccessRequest>[] = [
    {
      key: 'email',
      header: 'Email',
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'reason',
      header: 'Justification',
      render: (r) => (
        <span className="requests-view__reason" title={r.reason}>
          {r.reason.length > 80 ? r.reason.slice(0, 80) + '...' : r.reason}
        </span>
      ),
    },
    {
      key: 'requestedRole',
      header: 'Requested Role',
      render: (r) => <StatusBadge status={r.requestedRole} />,
    },
    {
      key: 'createdAt',
      header: 'Submitted',
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div className="requests-view__actions">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={(e) => {
              e.stopPropagation();
              setApproveTarget(r);
            }}
          >
            Approve
          </button>
          <button
            type="button"
            className="btn btn--danger btn--sm"
            onClick={(e) => {
              e.stopPropagation();
              setDenyTarget(r);
            }}
          >
            Deny
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Access Requests</h1>
        <div className="view__actions">
          <button
            className="btn btn--secondary"
            onClick={fetchRequests}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="alert alert--success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && requests.length === 0 && (
        <div className="view__empty">Loading access requests...</div>
      )}

      {!loading && requests.length === 0 && (
        <div className="view__empty">No pending access requests.</div>
      )}

      {requests.length > 0 && (
        <DataTable
          columns={columns}
          data={requests}
          keyExtractor={(r) => r.email}
          emptyMessage="No pending access requests"
        />
      )}

      {/* Approve confirmation dialog */}
      <ConfirmDialog
        isOpen={approveTarget !== null}
        title="Approve Access Request"
        message={`Approve access for ${approveTarget?.email ?? ''}? This will create an active user account with role "${approveTarget?.requestedRole ?? 'user'}".`}
        confirmLabel="Approve"
        onConfirm={handleApprove}
        onCancel={() => setApproveTarget(null)}
      />

      {/* Deny modal with reason */}
      <Modal
        isOpen={denyTarget !== null}
        onClose={() => {
          setDenyTarget(null);
          setDenyReason('');
        }}
        title="Deny Access Request"
      >
        <p className="confirm-dialog__message">
          Deny access request from {denyTarget?.email ?? ''}?
        </p>
        <div className="form-group">
          <label className="form-group__label" htmlFor="deny-reason">
            Reason (optional)
          </label>
          <textarea
            id="deny-reason"
            className="form-group__input"
            rows={3}
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Provide a reason for denying this request..."
          />
        </div>
        <div className="modal__actions">
          <button
            type="button"
            className="modal__cancel"
            onClick={() => {
              setDenyTarget(null);
              setDenyReason('');
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="modal__submit modal__submit--danger"
            onClick={handleDeny}
          >
            Deny Request
          </button>
        </div>
      </Modal>
    </div>
  );
}
